/**
 * @file src/room/followUser.js
 * @description Following someone from inside a room.
 *
 * The card already knows whether you follow this person — `viewerIsFollowing` rides on the same
 * `/users/card` payload the seat and the chat rail read — so this only has to flip it. There is
 * deliberately no "is following?" request: asking again for something we were just told is how a
 * roster of four people turns into eight requests.
 *
 * The write goes straight to the platform's own follow endpoint with the viewer's session, the
 * same way reporting a chat message does. The result is written back into the shared user-card
 * cache, so every surface showing that person — their seat, their chat rows — agrees at once.
 */

import { fetchJsonFromApi } from '../api/httpClient.js';
import { resolveViewerAuth } from '../auth/viewerAuth.js';
import { getUserCard, setUserCard } from './userCards.js';

/**
 * Toggle following a user. Optimistic: the cache flips immediately so the button responds to the
 * press, and rolls back if the write fails — a follow button that lies is worse than a slow one.
 *
 * @param {string} userId
 * @param {{ fetchApi?: typeof fetchJsonFromApi, resolveAuth?: typeof resolveViewerAuth }} [options]
 * @returns {Promise<{ ok: boolean, following: boolean }>}
 */
/**
 * One toggle per person at a time. The same person can be on screen twice (their seat and a chat
 * row), and two presses racing would each reconcile against their own stale "before" snapshot and
 * fight over the result.
 */
const inFlight = new Map();

export async function toggleUserFollow(userId, options = {}) {
  if (!userId) return { ok: false, following: false };
  const running = inFlight.get(userId);
  if (running) return running;
  const request = runToggle(userId, options).finally(() => inFlight.delete(userId));
  inFlight.set(userId, request);
  return request;
}

async function runToggle(userId, { fetchApi = fetchJsonFromApi, resolveAuth = resolveViewerAuth } = {}) {
  const before = getUserCard(userId);
  if (!before) return { ok: false, following: Boolean(before?.viewerIsFollowing) };

  const wasFollowing = before.viewerIsFollowing === true;
  const next = !wasFollowing;
  const delta = next ? 1 : -1;

  setUserCard(userId, {
    ...before,
    viewerIsFollowing: next,
    followersCount: Math.max(0, (before.followersCount ?? 0) + delta),
  });

  try {
    const { authToken } = (await resolveAuth()) ?? {};
    const response = await fetchApi('/follows/toggle', {
      method: 'POST',
      authToken,
      body: { targetType: 'user', targetId: userId },
    });
    if (!response?.ok) throw new Error('follow refused');
    // Trust the server's answer over ours when it bothers to give one. Disagreeing can only mean
    // it landed back on the state we started from, so the pre-press snapshot IS the correction —
    // no arithmetic, and no chance of the count drifting.
    const data = await response.json().catch(() => null);
    const settled = typeof data?.following === 'boolean' ? data.following : next;
    if (settled !== next) setUserCard(userId, before);
    return { ok: true, following: settled };
  } catch {
    setUserCard(userId, before);
    return { ok: false, following: wasFollowing };
  }
}
